import { useEffect, useRef, useState } from "react";
import { AudioWaveform, Cable, SlidersHorizontal } from "lucide-react";
import { Fader } from "./Fader";
import { Knob } from "./Knob";
import { PatchBay, sourceLabel } from "./PatchBay";

const H = 96;
const db = (v) => `${v > 0 ? "+" : ""}${v.toFixed(1)} dB`;
const pct = (v) => `${Math.round(v * 100)}%`;

const Meter = ({ setRef, testId }) => (
  <div className="relative w-1.5 rounded-sm bg-black overflow-hidden border border-zinc-900" style={{ height: H }} data-testid={testId}>
    <div ref={setRef} className="meter absolute inset-0" style={{ clipPath: "inset(100% 0 0 0)" }} />
  </div>
);

const Fx = ({ i, ch, onPatch }) => (
  <div className="grid grid-cols-2 gap-x-1 gap-y-1.5 w-full justify-items-center border-y border-zinc-800/80 py-1.5" data-testid={`mixer-fx-ch-${i + 1}`}>
    <Knob label="Hi" min={-12} max={12} value={ch.eq_high || 0} fmt={db} onChange={(v) => onPatch(i, { eq_high: v })} testId={`mixer-eq-high-ch-${i + 1}`} size={22} />
    <Knob label="Mid" min={-12} max={12} value={ch.eq_mid || 0} fmt={db} onChange={(v) => onPatch(i, { eq_mid: v })} testId={`mixer-eq-mid-ch-${i + 1}`} size={22} />
    <Knob label="Low" min={-12} max={12} value={ch.eq_low || 0} fmt={db} onChange={(v) => onPatch(i, { eq_low: v })} testId={`mixer-eq-low-ch-${i + 1}`} size={22} />
    <Knob label="Rev" value={ch.rev || 0} fmt={pct} color="#ec4899" onChange={(v) => onPatch(i, { rev: v })} testId={`mixer-rev-ch-${i + 1}`} size={22} />
    <Knob label="Echo" value={ch.echo || 0} fmt={pct} color="#39ff6a" onChange={(v) => onPatch(i, { echo: v })} testId={`mixer-echo-ch-${i + 1}`} size={22} />
  </div>
);

const Strip = ({ i, ch, options, onPatch, meterRef, showFx, lit, onAssign }) => (
  <div className={`snap-start shrink-0 flex-1 min-w-[60px] max-w-[88px] flex flex-col items-center gap-1.5 rounded-sm border bg-[#101014] px-1 py-1.5 transition-colors ${lit ? "border-[#39ff6a]" : "border-[#2d2d38]"}`} data-testid={`mixer-channel-${i + 1}`}>
    <span className="mono text-[9px] text-zinc-500">CH {String(i + 1).padStart(2, "0")}</span>
    <button onClick={onAssign} data-testid={`mixer-source-ch-${i + 1}`} title={sourceLabel(options, ch.source)}
      className={`w-full truncate rounded-[2px] border px-1 py-1 text-[9px] transition-colors ${ch.source ? "border-yellow-400/50 text-yellow-200 bg-yellow-400/5" : "border-zinc-800 text-zinc-500 hover:text-zinc-300"}`}>
      {ch.source ? sourceLabel(options, ch.source) : "+ Assign"}
    </button>
    {showFx && <Fx i={i} ch={ch} onPatch={onPatch} />}
    <div className="flex gap-1 w-full">
      <button onClick={() => onPatch(i, { mute: !ch.mute })} data-testid={`mixer-mute-btn-${i + 1}`}
        className={`flex-1 mono text-[9px] font-bold rounded-[2px] py-0.5 border transition-colors ${ch.mute ? "bg-pink-500 text-white border-pink-400 glow-pink" : "border-zinc-700 text-zinc-400 hover:text-pink-300"}`}>M</button>
      <button onClick={() => onPatch(i, { solo: !ch.solo })} data-testid={`mixer-solo-btn-${i + 1}`}
        className={`flex-1 mono text-[9px] font-bold rounded-[2px] py-0.5 border transition-colors ${ch.solo ? "bg-yellow-400 text-black border-yellow-300 glow-yellow" : "border-zinc-700 text-zinc-400 hover:text-yellow-300"}`}>S</button>
    </div>
    <div className="flex items-end gap-1">
      <Meter setRef={meterRef} testId={`mixer-meter-ch-${i + 1}`} />
      <Fader value={ch.volume} onChange={(v) => onPatch(i, { volume: v })} testId={`mixer-fader-ch-${i + 1}`} height={H} />
    </div>
    <span className="mono text-[9px] text-zinc-400">{Math.round(ch.volume * 100)}</span>
  </div>
);

const Master = ({ mixer, onChange, meterRef, showFx }) => (
  <div className="shrink-0 w-[76px] flex flex-col items-center gap-1.5 rounded-sm border border-yellow-400/40 bg-[#16140a] px-1.5 py-1.5" data-testid="mixer-master">
    <span className="mono text-[9px] font-bold text-yellow-300">MASTER</span>
    {showFx && (
      <div className="grid grid-cols-2 gap-1.5 justify-items-center border-y border-yellow-400/20 py-1.5 w-full">
        <Knob label="Rev" value={mixer.rev_return ?? 0.7} fmt={pct} color="#ec4899" onChange={(v) => onChange({ ...mixer, rev_return: v })} testId="mixer-rev-return" size={22} />
        <Knob label="Time" min={0.05} max={1.5} value={mixer.echo_time ?? 0.35} fmt={(v) => `${Math.round(v * 1000)} ms`} color="#39ff6a" onChange={(v) => onChange({ ...mixer, echo_time: v })} testId="mixer-echo-time" size={22} />
        <Knob label="Fdbk" max={0.9} value={mixer.echo_feedback ?? 0.35} fmt={pct} color="#39ff6a" onChange={(v) => onChange({ ...mixer, echo_feedback: v })} testId="mixer-echo-feedback" size={22} />
      </div>
    )}
    <div className="flex items-end gap-1 mt-auto">
      <Meter setRef={meterRef} testId="mixer-meter-master" />
      <Fader value={mixer.master} onChange={(v) => onChange({ ...mixer, master: v })} testId="mixer-fader-master" height={H} />
    </div>
    <span className="mono text-[9px] text-zinc-400">{Math.round(mixer.master * 100)}</span>
  </div>
);

export const Mixer = ({ engine, mixer, onChange, options }) => {
  const meters = useRef([]);
  const masterMeter = useRef(null);
  const [showFx, setShowFx] = useState(false);
  const [checking, setChecking] = useState(null);
  const [patchOpen, setPatchOpen] = useState(false);
  useEffect(() => {
    let raf;
    const tick = () => {
      engine.channels.forEach((c, i) => {
        const el = meters.current[i];
        if (el) el.style.clipPath = `inset(${(1 - engine.level(c.an)) * 100}% 0 0 0)`;
      });
      if (masterMeter.current) masterMeter.current.style.clipPath = `inset(${(1 - engine.level(engine.masterAn)) * 100}% 0 0 0)`;
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [engine]);

  // A mic, instrument rack or track lives on one channel only: assigning it clears it from other strips.
  const patch = (i, p) => onChange({
    ...mixer,
    channels: mixer.channels.map((c, j) => {
      if (j === i) return { ...c, ...p };
      return p.source && c.source === p.source ? { ...c, source: null } : c;
    }),
  });
  const soundcheck = () => { setChecking(0); engine.soundcheck((i) => setChecking(i < 0 ? null : i)); };
  const btn = "flex items-center gap-1.5 rounded-[3px] border px-2.5 py-1 mono text-[10px] uppercase tracking-wider transition-colors";
  return (
    <div className="rack rounded-md border border-[#2d2d38] p-2.5" data-testid="pa-mixer">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className="mono-label text-yellow-300">12-Channel PA · Host mix</span>
        <div className="ml-auto flex gap-2">
          <button onClick={() => setPatchOpen(true)} data-testid="mixer-assign-btn"
            className={`${btn} border-yellow-400/70 text-yellow-300 hover:bg-yellow-400/10`}>
            <Cable className="h-3.5 w-3.5" /> Assign channels
          </button>
          <button onClick={soundcheck} disabled={checking !== null} data-testid="mixer-soundcheck-btn"
            className={`${btn} ${checking !== null ? "border-[#39ff6a] text-[#39ff6a] glow-green" : "border-zinc-700 text-zinc-300 hover:border-[#39ff6a] hover:text-[#39ff6a]"}`}>
            <AudioWaveform className="h-3.5 w-3.5" /> {checking !== null ? `Checking CH ${checking + 1}` : "Soundcheck"}
          </button>
          <button onClick={() => setShowFx((v) => !v)} data-testid="mixer-fx-toggle-btn"
            className={`${btn} ${showFx ? "border-yellow-400 text-yellow-300" : "border-zinc-700 text-zinc-300 hover:border-yellow-400"}`}>
            <SlidersHorizontal className="h-3.5 w-3.5" /> EQ & FX
          </button>
        </div>
      </div>
      <div className="flex gap-2">
        <div className="flex-1 min-w-0 flex gap-1 overflow-x-auto snap-x snap-mandatory pb-1 no-scrollbar" data-testid="mixer-channel-strip">
          {mixer.channels.map((ch, i) => (
            <Strip key={i} i={i} ch={ch} options={options} onPatch={patch} showFx={showFx} lit={checking === i}
              onAssign={() => setPatchOpen(true)} meterRef={(el) => (meters.current[i] = el)} />
          ))}
        </div>
        <Master mixer={mixer} onChange={onChange} showFx={showFx} meterRef={(el) => (masterMeter.current = el)} />
      </div>
      <PatchBay open={patchOpen} onOpenChange={setPatchOpen} mixer={mixer} options={options} onPatch={patch} />
      <p className="mt-1 mono text-[9px] text-zinc-600 xl:hidden">Swipe for more channels →</p>
    </div>
  );
};
